import {
  useCallback,
  useRef,
  useState,
  DragEvent,
  MutableRefObject,
  ReactNode,
  useEffect,
  useMemo,
} from 'react';
import { Box, Snackbar, SnackbarContent, Typography } from '@mui/material';
import { formatTranslation } from 'intl';
import { CommonTranslationKeys } from 'intl/keys';
import { useIntl } from 'react-intl';
import { DocumentationViewItem } from 'store/documentation/model/types';
import { Color } from 'shared/utils/colors';
import { Identificable } from 'shared/types/commonView';
import { useExternalFilesDnd } from './useExternalFilesDnd';
import { useInternalRowDnd } from './useInternalRowDnd';
import { useDocumentationStore } from 'store/documentation';
import { getCurrentDirectoryName } from '../model';
import { DocumentationTranslationKeys } from 'intl/keys';
import {
  DOCUMENTATION_DND_TARGET_EVENT,
  DocumentationDnDTargetEventDetail,
} from '../model';

const DROP_MESSAGE_KEY = {
  internal: DocumentationTranslationKeys.dropFilesToMoveToDirectory,
  external: DocumentationTranslationKeys.dropFilesToDirectory,
} as const;

type RowIdGetter = (row: DocumentationViewItem) => string;

export function useDragAndDrop(params: {
  rows: DocumentationViewItem[];
  getRowId: RowIdGetter;
  onFilesDrop: (
    files: File[],
    hasDirs: boolean,
    targetDirectoryId: string,
    emptyDirectoryPaths: string[]
  ) => void;
  setParent?: (parent: Identificable) => void;
  selectElements?: (
    items: DocumentationViewItem[],
    opts: { ctrlKeyPressed: boolean }
  ) => void;
}): {
  containerProps: {
    onDragEnter: (e: DragEvent<HTMLDivElement>) => void;
    onDragOver: (e: DragEvent<HTMLDivElement>) => void;
    onDragLeave: (e: DragEvent<HTMLDivElement>) => void;
    onDrop: (e: DragEvent<HTMLDivElement>) => void;
  };
  rowProps: {
    onDragOver: (e: DragEvent<Element>) => void;
    onDragLeave: (e: DragEvent<Element>) => void;
    onDragStart: ((e: DragEvent<Element>) => void) | null;
    onDragEnd: (e: DragEvent<Element>) => void;
    onDrop: (e: DragEvent<Element>) => void;
  };
  isDraggingOver: boolean;
  dropTargetRef: MutableRefObject<{ id: string; name: string | null }>;
  snackbar: ReactNode;
} {
  const intl = useIntl();
  const { rows, getRowId, onFilesDrop, setParent, selectElements } =
    params;
  
  const currentDirectory = useDocumentationStore.use.currentDirectory();
  const currentDirectoryPath = useDocumentationStore.use.currentDirectoryPath();

  const initialDropTarget = {
    id: currentDirectory,
    name: getCurrentDirectoryName(currentDirectoryPath),
  };
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [isInternalDrag, setIsInternalDrag] = useState(false);
  const [, setDropTargetVersion] = useState(0);
  const dropTargetRef = useRef(initialDropTarget);

  useEffect(() => {
    const newId = currentDirectory;
    const newName = getCurrentDirectoryName(currentDirectoryPath);
    const prev = dropTargetRef.current;
    if (prev.id === newId && prev.name === newName) return;
    dropTargetRef.current = { id: newId, name: newName };
    setDropTargetVersion((v) => v + 1);
  }, [currentDirectory, currentDirectoryPath]);

  const updateDropTarget = useCallback(
    (name: string | null, id: string) => {
      const prev = dropTargetRef.current;
      if (prev.name === name && prev.id === id) return;
      dropTargetRef.current = { id, name };
      setDropTargetVersion((v) => v + 1);
    },
    []
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const onExternalTargetChange = (
      event: Event
    ): void => {
      const customEvent =
        event as CustomEvent<DocumentationDnDTargetEventDetail>;
      if (!customEvent.detail) {
        return;
      }

      updateDropTarget(customEvent.detail.name, customEvent.detail.id);
      setIsDraggingOver(true);
    };

    window.addEventListener(
      DOCUMENTATION_DND_TARGET_EVENT,
      onExternalTargetChange
    );

    return () => {
      window.removeEventListener(
        DOCUMENTATION_DND_TARGET_EVENT,
        onExternalTargetChange
      );
    };
  }, [updateDropTarget]);

  const handleExternalDragStateChange = useCallback((isDragging: boolean) => {
    setIsDraggingOver(isDragging);
    if (isDragging) {
      setIsInternalDrag(false);
    }
  }, []);

  const handleInternalDragStateChange = useCallback((isDragging: boolean) => {
    setIsDraggingOver(isDragging);
    if (isDragging) {
      setIsInternalDrag(true);
    }
  }, []);

  const getCurrentDropTarget = useCallback(() => {
    const { currentDirectory, currentDirectoryPath } =
      useDocumentationStore.getState();

    return {
      id: currentDirectory,
      name: getCurrentDirectoryName(currentDirectoryPath),
    };
  }, []);

  const externalDnD = useExternalFilesDnd({
    rows,
    getRowId,
    getCurrentDropTarget,
    onFilesDrop,
    dropTargetRef,
    onDragStateChange: handleExternalDragStateChange,
    onDropTargetChange: updateDropTarget,
  });

  const internalDnD = useInternalRowDnd({
    rows,
    getRowId,
    setParent,
    selectElements,
    onDragStateChange: handleInternalDragStateChange,
    onDropTargetChange: updateDropTarget,
  });

  const mergeRowDragOver = useCallback(
    (e: DragEvent<Element>) => {
      externalDnD.rowProps.onDragOver(e);
      internalDnD.rowProps.onDragOver(e);
    },
    [externalDnD.rowProps, internalDnD.rowProps]
  );

  const mergeRowDrop = useCallback(
    (e: DragEvent<Element>) => {
      externalDnD.rowProps.onDrop(e);
      internalDnD.rowProps.onDrop(e);
    },
    [externalDnD.rowProps, internalDnD.rowProps]
  );

  const dropMessage = useMemo(() => {
    return formatTranslation(
      intl,
      DROP_MESSAGE_KEY[isInternalDrag ? 'internal' : 'external']
    );
  }, [isInternalDrag, intl]);

  const snackbar = (
    <Snackbar
      open={isDraggingOver}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{ zIndex: 30000 }}
    >
      <SnackbarContent
        sx={{
          backgroundColor: Color.primaryOrange,
          borderRadius: 1,
          color: Color.black,
        }}
        message={
          <Box
            component='span'
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography
              component='span'
              variant='body2'
              sx={{ color: Color.black }}
            >
              {dropMessage}{' '}
              <Typography
                component='strong'
                variant='body2'
                sx={{ color: Color.black, fontWeight: 'bold' }}
              >
                {dropTargetRef.current.name ||
                  formatTranslation(intl, CommonTranslationKeys.documentation)}
              </Typography>
            </Typography>
          </Box>
        }
      />
    </Snackbar>
  );

  return {
    containerProps: externalDnD.containerProps,
    rowProps: {
      onDragOver: mergeRowDragOver,
      onDragLeave: externalDnD.rowProps.onDragLeave,
      onDragStart: internalDnD.rowProps.onDragStart,
      onDragEnd: internalDnD.rowProps.onDragEnd,
      onDrop: mergeRowDrop,
    },
    isDraggingOver,
    dropTargetRef,
    snackbar,
  };
}
